import {LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {QueryClientProvider} from "@tanstack/react-query";
import {ReactQueryDevtools} from "@tanstack/react-query-devtools";
import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap/dist/js/bootstrap';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {createBrowserRouter, RouterProvider,} from "react-router-dom";
import {queryClient} from "./api/queryClient";
import './App.css';
import {User} from "./Models/User";
import {LoginContext} from './contexts/LoginContext';
import {NotFound} from "./pages/NotFound";
import {AdminHome} from "./components/admin/AdminHome";
import {ProtectedRoute} from "./components/ProtectedRoute";
import {Login} from "./pages/Login";
import config, {logEnvironmentVariables} from './utils/config';
import {USER_STORAGE_KEY} from "./utils/constant";
import {UserHome} from "./components/user/UserHome";
import AdminGroupsTab from "./components/admin/groups/AdminGroupsTab";
import {SetPassword} from "./pages/SetPassword";
import ActivatePage from "./pages/activate";

function App() {

    const storageUser = localStorage.getItem(USER_STORAGE_KEY)
    const currentUserValue = storageUser ? new User(JSON.parse(storageUser)) : undefined
    const [currentUser, setCurrentUser] = useState<User | undefined>(currentUserValue)

    const updateCurrentUser = useCallback((value: User) => {
        if (value) localStorage.setItem(USER_STORAGE_KEY, value.toJsonString())
        setCurrentUser(value)
    }, [])

    // Load and verify environment variables at app startup
    useEffect(() => {
        //console.log('*** App component mounted - Loading environment variables ***');
        logEnvironmentVariables();
        if (!config.api.baseUrl) {
            console.error('⚠️ Warning: API Base URL not found in environment variables, using fallback');
        }
        if (config.environment === 'development') {
        }
    }, []);

    const userContextValues = useMemo(() => ({
        currentUser,
        setCurrentUser: (v: User) => updateCurrentUser(v)
    }), [currentUser, updateCurrentUser])

    const browserRouter = createBrowserRouter([
        {
            path: "/",
            children: [
                {
                    path: "",
                    element: <Login/>
                },
                {
                    path: "/activate",
                    element: <ActivatePage/>
                },
                {
                    path: "/set-password",
                    element: <SetPassword/>
                },
                {
                    path: "/user/dashboard",
                    element: <ProtectedRoute requiresAdmin={false}><UserHome/></ProtectedRoute>
                },
                // Routes d'administration - toutes protégées et nécessitent les droits admin
                {
                    path: "admin/dashboard",
                    element: <ProtectedRoute requiresAdmin={true}><AdminHome/></ProtectedRoute>,
                },
                {
                    path: "admin/projects",
                    element: <ProtectedRoute requiresAdmin={true}><AdminHome/></ProtectedRoute>
                },
                {
                    path: "admin/groups",
                    element: <ProtectedRoute requiresAdmin={true}><AdminHome/></ProtectedRoute>
                },
                {
                    path: "admin/groups/new",
                    element: <ProtectedRoute requiresAdmin={true}><AdminHome/></ProtectedRoute>
                },
                {
                    path: "admin/projects/new",
                    element: <ProtectedRoute requiresAdmin={true}><AdminHome/></ProtectedRoute>
                },
                {
                    path: "admin/users",
                    element: <ProtectedRoute requiresAdmin={true}><AdminHome/></ProtectedRoute>
                },
                {
                    path: "admin/users/new",
                    element: <ProtectedRoute requiresAdmin={true}><AdminHome/></ProtectedRoute>
                },
                {
                    path: "admin/participants/new",
                    element: <ProtectedRoute requiresAdmin={true}><AdminHome/></ProtectedRoute>
                },
                {
                    path: "admin/cash-in/new",
                    element: <ProtectedRoute requiresAdmin={true}><AdminHome/></ProtectedRoute>
                },
                {
                    path: "admin/cash-out/new",
                    element: <ProtectedRoute requiresAdmin={true}><AdminHome/></ProtectedRoute>
                },
                {
                    path: "admin/transactions",
                    element: <ProtectedRoute requiresAdmin={true}><AdminHome/></ProtectedRoute>
                },
                {
                    path: "admin/messaging",
                    element: <ProtectedRoute requiresAdmin={true}><AdminHome/></ProtectedRoute>
                },
                {
                    path: "/user/messages",
                    element: <ProtectedRoute requiresAdmin={false}><UserHome/></ProtectedRoute>
                },
                {
                    path: "*",
                    element: <NotFound/>
                }
            ]
        }
    ])
    return (
        <QueryClientProvider client={queryClient}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <LoginContext.Provider value={userContextValues}>
                    <RouterProvider router={browserRouter}/>
                </LoginContext.Provider>
            </LocalizationProvider>
            <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>)
}

export default App;
